import { Fragment, useCallback, useEffect, useRef, useState } from 'react'

import { Listbox, ListboxButton, ListboxOption, ListboxOptions, Transition } from '@headlessui/react'
import { clsx } from 'clsx'
import { IconChevronDown, IconChevronUp, IconFilterLight } from 'packages/component-library/dist'

interface SelectItem {
    id: string;
    name: string;
}

interface SelectProductMobileProps {
    defaultValue: SelectItem,
    options: (SelectItem)[],
    accentColor?: string,
    name?: string,
    placeholder?: string,
    onChange?: (option: SelectItem) => void,
  }

export const SelectProductMobile = ({ 
  defaultValue,
  options,
  name = '',
  placeholder = '',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- kept for backward-compatible prop signature; border color is now driven solely by --sp-color-accent
  accentColor = '',
  onChange: onChangeProps,
}: SelectProductMobileProps) => {
  const [value, setValue] = useState<SelectItem>(defaultValue)
  
  const onChange = useCallback((option: SelectItem) => {
    setValue(option)
    onChangeProps?.(option)
  }, [])
  
  const buttonRef = useRef<HTMLButtonElement>(null)
  const [buttonWidth, setButtonWidth] = useState<number | null>(null)

  useEffect(() => {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth)
    }
  }, [])

  return (
    <Listbox
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      name={name}
    >
      {({ open }) => (
        <>
          <ListboxButton
            ref={buttonRef}
            className={clsx(
              'sp-search-mobile-product-selector flex justify-between items-center w-full cursor-pointer rounded text-left bg-(--sp-color-surface-sunken)',
              'h-10 text-body2 pl-3 py-2 border',
              open ? 'border-(--sp-color-accent)' : ' border-(--sp-color-border-strong)',
            )}
          >
            <span className="mr-2 text-(--sp-color-text-muted)"><IconFilterLight width={16} height={16} /></span>
            <span className="truncate text-(--sp-color-text)">{value?.name || placeholder}</span>
            <span className="pr-3 ml-auto text-(--sp-color-text-muted)">{open ? <IconChevronUp width={16} height={16} /> : <IconChevronDown width={16} height={16} />}</span>
          </ListboxButton>
          <Transition
            show={open}
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="absolute flex flex-col shadow-[0_3px_6px_0_rgba(0,0,0,0.18)] rounded z-50 mt-24" style={{ width: buttonWidth ?? 'auto' }}>
              <ListboxOptions className="sp-search-mobile-product-selector-options z-50 w-full overflow-x-hidden overflow-y-auto rounded max-h-60 focus:outline-none">
                {options.map((option: SelectItem) => (
                  <ListboxOption
                    key={option.id}
                    className={
                      clsx(
                        'sp-search-mobile-product-selector-option',
                        'hover:text-(--sp-color-text)',
                        (value?.id === option.id
                          ? 'text-(--sp-color-text)'
                          : 'text-(--sp-color-text-muted)'),
        
                        'hover:bg-(--sp-color-surface-hover)',
                        (value?.id === option.id
                          ? ('bg-(--sp-color-surface-raised)')
                          : 'bg-(--sp-color-surface)'),
                        'cursor-pointer select-none py-1.5 px-4 text-body2 break-words',
                      )
                    }
                    value={option}
                  >
                    {option.name}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </div>
          </Transition>
        </>
      )}
    </Listbox>
  )
}